/**
 * Hub sheets — the layer under the lobby's four tiles.
 *
 * The hub prints four destinations and nothing else. Start Adventure and Play
 * with Friends open a sheet each, carrying the controls the old command cards
 * used to print permanently (Tutorial / Adventure; Create / Join / code). The
 * Community pill in the account bar opens a third (Discord, Invite a friend).
 *
 * This module owns visibility, focus and dismissal, and nothing else. It never
 * starts a game, creates a room or renders text: those stay with lobby.js,
 * referrals.js and quests.js, which keep the SAME control ids inside the
 * sheets, so every listener, guard and test bound to `#solo-adventure-btn` or
 * `#create-room-btn` is untouched by where the control is drawn.
 *
 * Contract (tests/unit/ui/hubSheets.test.js):
 *   - a sheet is `#hub-<name>-sheet[data-hub-sheet=<name>]`, closed by the
 *     `hidden` attribute, open with `.is-open`; its opener is any
 *     `[data-hub-open=<name>]`, which mirrors the state in `aria-expanded`;
 *   - one sheet at a time; opening another closes the first;
 *   - Escape, a press on the backdrop, and any `[data-hub-close]` close it, and
 *     focus returns to the opener that opened it;
 *   - a sheet that holds a LIVE ROOM (`[data-room-state]` created/joined) is
 *     locked: none of the above dismiss it, and no other sheet opens over it.
 *     Go Back inside the sheet is the exit. `closeAll()` — the lobby leaving
 *     for a match — is the one thing that closes a locked sheet;
 *   - Tab cycles inside the open sheet.
 */

let HubSheets = {};

HubSheets = (function () {
    const FOCUSABLE = [
        'a[href]', 'button:not([disabled])', 'input:not([disabled])',
        'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    /** @type {Map<string, HTMLElement>} */
    const sheets = new Map();
    /** @type {string|null} */
    let current = null;
    /** @type {HTMLElement|null} */
    let returnTo = null;
    let bound = false;

    function resolve() {
        sheets.clear();
        document.querySelectorAll('[data-hub-sheet]').forEach((node) => {
            const name = node.getAttribute('data-hub-sheet');
            if (name && node instanceof HTMLElement) sheets.set(name, node);
        });
        return sheets.size > 0;
    }

    function openers(name) {
        return Array.from(document.querySelectorAll(`[data-hub-open="${name}"]`));
    }

    function isVisible(element) {
        if (!(element instanceof HTMLElement)) return false;
        if (element.hidden) return false;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
    }

    function focusables(sheet) {
        return Array.from(sheet.querySelectorAll(FOCUSABLE)).filter(isVisible);
    }

    /** The Friends sheet holds the room card; a live room locks it. */
    function isLocked(name = current) {
        if (!name) return false;
        const sheet = sheets.get(name);
        const room = sheet?.querySelector('[data-room-state]');
        const state = room?.getAttribute('data-room-state');
        return state === 'created' || state === 'joined';
    }

    function syncLock() {
        sheets.forEach((sheet, name) => {
            sheet.classList.toggle('is-locked', name === current && isLocked(name));
        });
    }

    function setExpanded(name, expanded) {
        openers(name).forEach((opener) => opener.setAttribute('aria-expanded', String(expanded)));
    }

    function isOpen() {
        return current !== null;
    }

    /**
     * @param {string} name
     * @param {{ trigger?: HTMLElement|null, focus?: boolean }} [options]
     */
    function open(name, { trigger = null, focus = true } = {}) {
        const sheet = sheets.get(name);
        if (!sheet) return false;
        if (current === name) {
            syncLock();
            return true;
        }
        // A live room keeps its sheet; nothing opens over it.
        if (current && isLocked(current)) return false;
        if (current) close({ restoreFocus: false });

        current = name;
        returnTo = trigger || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
        sheet.hidden = false;
        sheet.classList.add('is-open');
        setExpanded(name, true);
        document.body.classList.add('hub-sheet-open');
        syncLock();
        if (focus) {
            // The first real control, never the × — a player who opened
            // "Start Adventure" should be one Enter away from the duel.
            const targets = focusables(sheet).filter((node) => !node.hasAttribute('data-hub-close'));
            const target = targets[0] || focusables(sheet)[0] || sheet;
            if (target instanceof HTMLElement) target.focus({ preventScroll: true });
        }
        return true;
    }

    /**
     * @param {{ restoreFocus?: boolean, force?: boolean }} [options]
     */
    function close({ restoreFocus = true, force = false } = {}) {
        if (!current) return false;
        if (!force && isLocked(current)) return false;
        const name = current;
        const sheet = sheets.get(name);
        current = null;
        if (sheet) {
            sheet.classList.remove('is-open', 'is-locked');
            sheet.hidden = true;
        }
        setExpanded(name, false);
        document.body.classList.remove('hub-sheet-open');
        const target = returnTo;
        returnTo = null;
        if (restoreFocus && target && target.isConnected) {
            target.focus({ preventScroll: true });
        }
        return true;
    }

    /** The lobby is leaving (a match starts, the view changes): everything shuts. */
    function closeAll() {
        close({ restoreFocus: false, force: true });
        sheets.forEach((sheet, name) => {
            if (!sheet.hidden) {
                sheet.hidden = true;
                sheet.classList.remove('is-open', 'is-locked');
                setExpanded(name, false);
            }
        });
        document.body.classList.remove('hub-sheet-open');
    }

    function toggle(name, trigger) {
        if (current === name) return close();
        return open(name, { trigger });
    }

    /**
     * A dialog opened FROM a sheet (the summoner picker, the invitation card,
     * the store) lives outside it in the DOM and takes the keyboard. Escape
     * and Tab there belong to that dialog: the sheet answers only while the
     * focus is inside it, or nowhere in particular.
     */
    function keyboardOwnedElsewhere(sheet) {
        const active = document.activeElement;
        if (!(active instanceof Element) || active === document.body) return false;
        return !sheet.contains(active);
    }

    function onKeydown(event) {
        if (!current) return;
        const sheet = sheets.get(current);
        if (!sheet) return;
        if (keyboardOwnedElsewhere(sheet)) return;
        if (event.key === 'Escape') {
            if (close()) {
                event.preventDefault();
                event.stopPropagation();
            }
            return;
        }
        if (event.key !== 'Tab') return;
        const nodes = focusables(sheet);
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement;
        const inside = active instanceof Node && sheet.contains(active);
        if (event.shiftKey) {
            if (!inside || active === first) {
                event.preventDefault();
                last.focus({ preventScroll: true });
            }
        } else if (!inside || active === last) {
            event.preventDefault();
            first.focus({ preventScroll: true });
        }
    }

    function onPointerDown(event) {
        if (!current) return;
        const sheet = sheets.get(current);
        if (!sheet) return;
        // The backdrop is the sheet root itself; anything inside the card stays.
        if (event.target === sheet) {
            if (close()) event.preventDefault();
        }
    }

    function bindOpeners() {
        document.querySelectorAll('[data-hub-open]').forEach((opener) => {
            if (!(opener instanceof HTMLElement)) return;
            if (opener.dataset.hubBound === 'true') return;
            opener.dataset.hubBound = 'true';
            const name = opener.getAttribute('data-hub-open') || '';
            opener.addEventListener('click', (event) => {
                event.preventDefault();
                toggle(name, opener);
            });
        });
        sheets.forEach((sheet) => {
            sheet.querySelectorAll('[data-hub-close]').forEach((button) => {
                if (!(button instanceof HTMLElement)) return;
                if (button.dataset.hubBound === 'true') return;
                button.dataset.hubBound = 'true';
                button.addEventListener('click', (event) => {
                    event.preventDefault();
                    close();
                });
            });
        });
    }

    function init() {
        if (!resolve()) return false;
        bindOpeners();
        if (!bound) {
            bound = true;
            document.addEventListener('keydown', onKeydown, true);
            document.addEventListener('pointerdown', onPointerDown, true);
        }
        return true;
    }

    return {
        init,
        open,
        close,
        closeAll,
        toggle,
        isOpen,
        isLocked,
        syncLock,
        current: () => current,
    };
})();

export { HubSheets };
